import { useState, useEffect, useCallback } from 'react';
import { reloadApp } from '../utils/reload';

declare const __FRONTEND_VERSION__: string;

const VERSION_URL = (import.meta.env.BASE_URL || '/') + 'version.txt';
const POLL_INTERVAL_MS = 15_000;

/** Build-time version baked into the JS bundle */
const BUILD_VERSION: string | null =
  typeof __FRONTEND_VERSION__ !== 'undefined' && __FRONTEND_VERSION__ !== 'dev'
    ? __FRONTEND_VERSION__
    : null;

/**
 * Clear all service-worker caches and reload.
 * Does NOT unregister the SW — that would kick a standalone PWA
 * out into the browser.  The new SW (with `self.skipWaiting()`)
 * activates automatically, so a plain reload is enough.
 */
export async function forceUpdate(): Promise<void> {
  console.log('[reload] forceUpdate called');

  // Clear caches with timeout — don't let a hanging Cache API block the reload
  try {
    const clearCaches = async () => {
      const keys = await caches.keys();
      console.log('[reload] clearing', keys.length, 'caches');
      await Promise.all(keys.map((k) => caches.delete(k)));
      console.log('[reload] caches cleared');
    };

    await Promise.race([
      clearCaches(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 3000),
      ),
    ]);
  } catch (err) {
    console.warn('[reload] cache clearing failed or timed out:', err);
  }

  // Reload from network
  reloadApp();
}

/**
 * Fetches the app version from version.txt deployed by CI/CD.
 * Displays the BUILD-TIME version (actual running code), not the remote version.
 * Sets `updateAvailable` when the remote version differs from the build version.
 * Polls every 15s to detect new deployments.
 */
export function useVersion() {
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const fetchVersion = useCallback(async () => {
    try {
      const res = await fetch(VERSION_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const text = (await res.text()).trim();
      if (!text) return;

      setRemoteVersion(text);

      // If we have a build version and it doesn't match remote, update is available
      if (BUILD_VERSION && text !== BUILD_VERSION) {
        setUpdateAvailable(true);
      }
    } catch {
      // network error — leave current value as-is
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    const id = setInterval(fetchVersion, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchVersion]);

  // Display the BUILD version (what's actually running), fall back to remote
  return { version: BUILD_VERSION || remoteVersion, updateAvailable };
}
